import React, { useEffect, useRef, useState } from 'react';
import './BetaAlert.scss';

export type BetaAlertSeverity = 'success' | 'error' | 'warning';

interface BetaAlertProps {
  severity: BetaAlertSeverity;
  title: string;
  message: React.ReactNode;
  detail?: string;
  onClose?: () => void;
  /** Closes the alert automatically after this many ms (paused while hovered/focused). */
  autoHideDuration?: number;
  'data-testid'?: string;
}

const SuccessIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="11" fill="currentColor" />
    <path
      d="M7 12.5l3.2 3.2L17 9"
      fill="none"
      stroke="#fff"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ErrorIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle
      cx="12"
      cy="12"
      r="10.5"
      fill="var(--beta-alert-icon-fill)"
      stroke="var(--beta-alert-icon-stroke)"
      strokeWidth="1.5"
    />
    <path
      d="M12 6.5v7"
      stroke="var(--beta-alert-icon-stroke)"
      strokeWidth="2.4"
      strokeLinecap="round"
    />
    <circle cx="12" cy="17.2" r="1.4" fill="var(--beta-alert-icon-stroke)" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M6 6l12 12M18 6L6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const useAutoHide = (duration: number | undefined, onClose?: () => void) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const remaining = useRef(duration ?? 0);
  const startedAt = useRef(0);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!duration || paused) return undefined;
    startedAt.current = Date.now();
    const timer = setTimeout(() => closeRef.current?.(), remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current -= Date.now() - startedAt.current;
    };
  }, [duration, paused]);

  // Pause the countdown while the alert is hovered or focused. Native listeners keep the
  // alert a plain (non-interactive) live region.
  useEffect(() => {
    const node = containerRef.current;
    if (!duration || !node) return undefined;
    const pause = () => setPaused(true);
    const resume = () => setPaused(false);
    const events: Array<[string, () => void]> = [
      ['mouseenter', pause],
      ['mouseleave', resume],
      ['focusin', pause],
      ['focusout', resume],
    ];
    events.forEach(([name, handler]) => node.addEventListener(name, handler));
    return () =>
      events.forEach(([name, handler]) =>
        node.removeEventListener(name, handler),
      );
  }, [duration]);

  return { containerRef, paused };
};

const BetaAlert: React.FC<BetaAlertProps> = ({
  severity,
  title,
  message,
  detail,
  onClose,
  autoHideDuration,
  'data-testid': testId,
}) => {
  const autoHide = autoHideDuration && onClose ? autoHideDuration : undefined;
  const { containerRef, paused } = useAutoHide(autoHide, onClose);

  return (
    <div
      ref={containerRef}
      className={`beta-alert beta-alert--${severity}`}
      role={severity === 'success' ? 'status' : 'alert'}
      aria-live={severity === 'success' ? 'polite' : 'assertive'}
      data-testid={testId}
    >
      <div className="beta-alert__heading">
        <span className="beta-alert__icon">
          {severity === 'success' ? <SuccessIcon /> : <ErrorIcon />}
        </span>
        <p className="beta-alert__title">{title}</p>
        {onClose && (
          <button
            type="button"
            className="beta-alert__close"
            onClick={onClose}
            aria-label="Dismiss notification"
          >
            <CloseIcon />
          </button>
        )}
      </div>
      <div className="beta-alert__message">{message}</div>
      {detail && <p className="beta-alert__detail">{detail}</p>}
      {autoHide && (
        <span
          className="beta-alert__progress"
          style={{
            animationDuration: `${autoHide}ms`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
          aria-hidden="true"
          data-testid="beta-alert-progress"
        />
      )}
    </div>
  );
};

export default BetaAlert;
